import {
	Briefcase,
	Calculator,
	CalendarDays,
	FileText,
	GraduationCap,
	type LucideIcon,
	Mail,
	Newspaper,
	PlayCircle,
	User,
	Users,
} from 'lucide-react'
import { linkTarget, trackClickOut } from '#app/utils/plausible.client.js'
import { GOOGLE_FORMS_FEEDBACK_URL } from '#app/utils/settings.js'

type ServiceLink = {
	label: string
	href: string
	icon: LucideIcon
}

export function ServiceMenu({
	isSignedIn,
	authUrl,
}: {
	isSignedIn: boolean
	authUrl: string
}) {
	const serviceLinks: ServiceLink[] = [
		// Order mirrors the internetmedicin.se hamburger menu (minus AI-assistent,
		// since we're already in the assistant). Labels intentionally differ
		// where this app uses shorter names (PM, ICD-koder).
		{
			label: 'PM',
			href: 'https://www.internetmedicin.se/specialistomraden',
			icon: Users,
		},
		{
			label: 'Utbildning',
			href: 'https://www.internetmedicin.se/kurser',
			icon: GraduationCap,
		},
		{
			label: 'Jobb',
			href: 'https://jobb.internetmedicin.se',
			icon: Briefcase,
		},
		{
			label: 'ICD-koder',
			href: 'https://verktyg.internetmedicin.se',
			icon: Calculator,
		},
		{
			label: 'Kongressrapporter',
			href: 'https://www.internetmedicin.se/rapporter',
			icon: FileText,
		},
		{
			label: 'Instruktionsfilmer',
			href: 'https://www.internetmedicin.se/instruktionsfilmer',
			icon: PlayCircle,
		},
		{
			label: 'Artiklar',
			href: 'https://www.internetmedicin.se/artiklar',
			icon: Newspaper,
		},
		{
			label: 'Kalender',
			href: 'https://www.internetmedicin.se/kalender',
			icon: CalendarDays,
		},
		isSignedIn
			? {
					label: 'Min sida',
					href: `${authUrl}/profile`,
					icon: User,
				}
			: {
					label: 'Logga in',
					href: `${authUrl}/?utm_source=assistent.internetmedicin.se&utm_medium=navbar&utm_campaign=login&next=sub-assistent`,
					icon: User,
				},
		{
			label: 'Nyhetsbrev',
			href: `${authUrl}/prenumerera-nyhetsbrev`,
			icon: Mail,
		},
	]
	return (
		// Every link in the menu: one click_out (position menu) per click, caught
		// here instead of on each link (tracking map in plausible.client.ts).
		<div
			className="w-[16rem] lg:w-[24rem]"
			onClick={e => {
				const href = (e.target as HTMLElement)
					.closest('a')
					?.getAttribute('href')
				const link = href ? linkTarget(href) : null
				if (href && link)
					trackClickOut({
						destinationUrl: href,
						destinationSource: link.source,
						isInternal: link.isInternal,
						position: 'menu',
						// The menu is only shown in the full app (never in embed).
						location: 'assistent_start_page',
					})
			}}
		>
			<div className="grid grid-cols-2 gap-2 p-4 lg:gap-4 lg:p-7">
				{serviceLinks.map(link => (
					<ServiceLinkItem key={link.href} link={link} />
				))}
			</div>
			<a
				href={GOOGLE_FORMS_FEEDBACK_URL}
				target="_blank"
				rel="noopener noreferrer"
				className="group mx-4 mb-3 block rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-center text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
			>
				<span className="lg:hidden">Hjälp oss förbättra</span>
				<span className="hidden lg:inline">
					Hjälp oss förbättra! Ge gärna feedback
				</span>{' '}
				<span className="inline-block transition-transform group-hover:translate-x-0.5">
					→
				</span>
			</a>
			<nav className="flex flex-wrap justify-center gap-x-4 gap-y-1 px-4 pb-3 text-xs">
				<a
					href="https://www.internetmedicin.se/om-internetmedicin"
					className="text-gray-500 transition hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
				>
					Om oss
				</a>
				<a
					href="https://www.internetmedicin.se/terms"
					className="text-gray-500 transition hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
				>
					Villkor
				</a>
				<a
					href="https://www.internetmedicin.se/privacy"
					className="text-gray-500 transition hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
				>
					Integritetspolicy
				</a>
			</nav>
			<div className="border-t border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800">
				<a
					href="https://www.internetmedicin.se/annonsera"
					className="block rounded-md px-4 py-3 text-center text-xs font-medium text-gray-900 transition hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700 lg:text-sm"
				>
					Annonsera på Internetmedicin
				</a>
			</div>
		</div>
	)
}

function ServiceLinkItem({ link }: { link: ServiceLink }) {
	const Icon = link.icon
	return (
		<a
			href={link.href}
			className="flex flex-col items-center rounded-lg p-2 transition hover:bg-gray-100 dark:hover:bg-gray-700"
		>
			<div className="flex h-8 w-8 items-center justify-center text-gray-700 dark:text-gray-400 lg:h-12 lg:w-12">
				<Icon
					className="h-8 w-8 lg:h-12 lg:w-12"
					strokeWidth={1.5}
					aria-hidden="true"
				/>
			</div>
			<span className="mt-1 text-center text-xs font-normal text-gray-700 dark:text-gray-400 lg:mt-2 lg:text-sm">
				{link.label}
			</span>
		</a>
	)
}
